import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import { MarketingAttributionEvent } from "@redotech/redo-model/marketing";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { ChannelFilter } from "./marketing-channel-filter";

const attributionEventOptions: TextRedoListItem<MarketingAttributionEvent>[] = [
  {
    id: "click",
    type: "text",
    text: "Clicks",
    supportingText: "Email & SMS clicks",
    value: "click",
  },
  {
    id: "open",
    type: "text",
    text: "Opens",
    supportingText: "Email opens only",
    value: "open",
  },
  {
    id: "delivery",
    type: "text",
    text: "Delivery",
    supportingText: "SMS delivery only",
    value: "delivery",
  },
];

export const MarketingAttributionEventsFilter = memo(
  function MarketingAttributionEventsFilter({
    selectedEvents,
    channelFilter,
    onChange,
  }: {
    selectedEvents: MarketingAttributionEvent[];
    channelFilter: ChannelFilter;
    onChange: (events: MarketingAttributionEvent[]) => void;
  }) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownAnchorRef, setDropdownAnchorRef] =
      useState<HTMLButtonElement | null>(null);

    // Filter options based on channel selection
    const availableOptions = useMemo(() => {
      if (channelFilter === "email") {
        // Email channel: exclude delivery (SMS only)
        return attributionEventOptions.filter(
          (option) => option.value !== "delivery",
        );
      } else if (channelFilter === "sms") {
        // SMS channel: exclude opens (email only)
        return attributionEventOptions.filter(
          (option) => option.value !== "open",
        );
      }
      return attributionEventOptions;
    }, [channelFilter]);

    // Generate display text based on selected events
    const getDisplayText = () => {
      if (selectedEvents.length === 0) {
        return "None selected";
      }
      if (selectedEvents.length === availableOptions.length) {
        return "All events";
      }
      if (selectedEvents.length === 1) {
        const option = availableOptions.find(
          (opt) => opt.value === selectedEvents[0],
        );
        return option?.text || "1 event";
      }
      return `${selectedEvents.length} events`;
    };

    return (
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Type
        </Text>
        <RedoButton
          hierarchy="secondary"
          IconTrailing={ChevronDownIcon}
          onClick={() => setDropdownOpen(!dropdownOpen)}
          ref={setDropdownAnchorRef}
          text={getDisplayText()}
        />
        <RedoMultiselectDropdown<MarketingAttributionEvent>
          dropdownAnchor={dropdownAnchorRef}
          dropdownOpen={dropdownOpen}
          fitToAnchor={false}
          items={availableOptions}
          selectedItems={availableOptions.filter((option) =>
            selectedEvents.includes(option.value),
          )}
          setDropdownOpen={setDropdownOpen}
          setSelectedItems={(items) =>
            onChange(items.map((item) => item.value))
          }
          size="xs"
        />
      </Flex>
    );
  },
);

export function getEmptyStateMessageForEvents(
  selectedEvents: MarketingAttributionEvent[],
): string {
  if (selectedEvents.length === 0) {
    return "No attribution events selected. Please select at least one event type.";
  }
  return "No data available for the selected attribution events and date range.";
}
